"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowLeft, Target, TrendingUp, Zap, HelpCircle } from "lucide-react";

export default function SalesPipelineCalculator() {
  const [opportunities, setOpportunities] = useState(50);
  const [dealValue, setDealValue] = useState(50000);
  const [winRate, setWinRate] = useState(20);
  const [cycleLength, setCycleLength] = useState(60);

  // Math logic
  // Pipeline Velocity = (Opps * Avg Deal Value * Win Rate %) / Cycle Length
  const rateFraction = parseFloat(winRate) / 100;
  const velocityPerDay = (opportunities * dealValue * rateFraction) / cycleLength;
  const monthlyRevenue = velocityPerDay * 30;
  const annualRevenue = velocityPerDay * 365;

  // Format helper
  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const tips = [
    {
      title: "Shorten Sales Cycle",
      desc: "Reducing cycle length from 60 to 45 days increases daily pipeline velocity by 33%. Automate follow-ups to keep leads moving.",
    },
    {
      title: "Boost Win Rate %",
      desc: "A 5% increase in your win rate increases your overall revenue output by 25%. Focus on qualifying high-value leads earlier.",
    },
    {
      title: "Increase Deal Value",
      desc: "Nurturing high-intent enterprise accounts increases average ticket size. Track interactions meticulously in your CRM.",
    },
  ];

  return (
    <div className="overflow-x-hidden w-full bg-[#FAFAFA] min-h-screen pb-16">
      {/* Header Bar */}
      <div className="relative w-full border-b border-[#E5E5E5] bg-white py-6">
        <div className="max-w-[1160px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12 flex items-center justify-between">
          <Link
            href="/tools"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#525252] hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
          <span className="text-[12px] text-[#525252] uppercase tracking-wider font-bold bg-[#F5F5F5] px-3 py-1 rounded-full">
            Sales Funnel Metric
          </span>
        </div>
      </div>

      <div className="max-w-[1160px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12 py-10">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-[#171717] mb-2 tracking-tight">
            Sales Pipeline Velocity Calculator
          </h1>
          <p className="text-sm sm:text-base text-[#525252]">
            Calculate the revenue flowing through your sales pipeline each day and forecast monthly sales volume.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left panel: Inputs */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-[#E5E5E5] rounded-[24px] p-6 sm:p-8 shadow-sm space-y-6">
              {/* Opportunities input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-[#171717]">
                    Active Opportunities / Leads
                  </label>
                  <span className="text-sm font-bold text-neutral-800 bg-[#F5F5F5] px-2.5 py-0.5 rounded">
                    {opportunities}
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="500"
                  step="5"
                  value={opportunities}
                  onChange={(e) => setOpportunities(parseInt(e.target.value))}
                  className="w-full accent-black cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>5 Opps</span>
                  <span>500 Opps</span>
                </div>
              </div>

              {/* Deal value input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-[#171717]">
                    Average Deal Value (₹)
                  </label>
                  <span className="text-sm font-bold text-neutral-800 bg-[#F5F5F5] px-2.5 py-0.5 rounded">
                    {formatCurrency(dealValue)}
                  </span>
                </div>
                <input
                  type="range"
                  min="5000"
                  max="500000"
                  step="5000"
                  value={dealValue}
                  onChange={(e) => setDealValue(parseFloat(e.target.value))}
                  className="w-full accent-black cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>₹5,000</span>
                  <span>₹5,00,000</span>
                </div>
              </div>

              {/* Win rate input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-[#171717]">
                    Average Win Rate (%)
                  </label>
                  <span className="text-sm font-bold text-neutral-800 bg-[#F5F5F5] px-2.5 py-0.5 rounded">
                    {winRate}%
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="100"
                  step="1"
                  value={winRate}
                  onChange={(e) => setWinRate(parseInt(e.target.value))}
                  className="w-full accent-black cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>5%</span>
                  <span>100%</span>
                </div>
              </div>

              {/* Sales cycle input */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-semibold text-[#171717]">
                    Sales Cycle Length (Days)
                  </label>
                  <span className="text-sm font-bold text-neutral-800 bg-[#F5F5F5] px-2.5 py-0.5 rounded">
                    {cycleLength} Days
                  </span>
                </div>
                <input
                  type="range"
                  min="7"
                  max="180"
                  step="1"
                  value={cycleLength}
                  onChange={(e) => setCycleLength(parseInt(e.target.value))}
                  className="w-full accent-black cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
                  <span>7 Days</span>
                  <span>180 Days</span>
                </div>
              </div>
            </div>

            {/* Funnel optimization tips */}
            <div className="bg-white border border-[#E5E5E5] rounded-[24px] p-6 shadow-sm">
              <h3 className="text-base font-bold text-[#171717] mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-neutral-400" />
                Pipeline Optimization Strategy
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {tips.map((tip, idx) => (
                  <div key={idx} className="space-y-2">
                    <span className="text-[11px] font-bold text-[#171717] uppercase tracking-wider block">
                      {tip.title}
                    </span>
                    <p className="text-xs text-[#525252] leading-relaxed">
                      {tip.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel: Metric Cards */}
          <div className="space-y-6">
            {/* Primary output */}
            <div className="bg-neutral-900 text-white rounded-[24px] p-6 sm:p-8 shadow-md">
              <h3 className="text-sm font-bold uppercase tracking-wider text-neutral-400 mb-6 flex items-center gap-1.5">
                <Target className="w-4 h-4 text-[#10B981]" />
                Pipeline Velocity
              </h3>

              <div className="mb-6">
                <span className="text-3xl sm:text-4xl font-extrabold text-white">
                  {formatCurrency(velocityPerDay)}
                </span>
                <span className="text-xs text-neutral-400 block mt-1">
                  Revenue flowing into the pipeline per day
                </span>
              </div>

              <div className="border-t border-neutral-800 pt-6 space-y-4 text-sm">
                <div className="flex justify-between text-neutral-400">
                  <span>Forecasted Monthly Sales</span>
                  <span className="font-semibold text-white">{formatCurrency(monthlyRevenue)}</span>
                </div>
                <div className="flex justify-between text-neutral-400">
                  <span>Forecasted Annual Sales</span>
                  <span className="font-semibold text-white">{formatCurrency(annualRevenue)}</span>
                </div>
              </div>
            </div>

            {/* CRM Funnel CTA */}
            <div className="bg-white border border-[#E5E5E5] rounded-[24px] p-6 shadow-sm text-center">
              <TrendingUp className="w-8 h-8 text-neutral-900 mx-auto mb-3" />
              <h4 className="text-base font-bold text-[#171717] mb-2">
                Accelerate Pipeline Deals
              </h4>
              <p className="text-xs text-[#525252] leading-relaxed mb-6">
                Never lose follow-ups. Track client emails, assign tasks, schedule visits, and monitor real-time win rates with ABSS CRM.
              </p>
              <Link
                href="/contact"
                className="block w-full py-2.5 bg-black text-white text-xs font-semibold rounded-lg hover:bg-neutral-800 transition-colors shadow-sm text-center"
              >
                Try ABSS CRM Free
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
